import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { checkScheduleTimes, HOUSEKEEPING_ERROR_MESSAGES } from '@wodi/domain';
import type { HousekeepingAreaView, HousekeepingScheduleView } from '@wodi/shared';
import { Button, Checkbox, LoadingBlock, Notice, Section, Tag, TextField } from '@wodi/ui';
import { useState } from 'react';

import {
  createArea,
  createSchedule,
  housekeepingAreasQuery,
  housekeepingKeys,
  updateArea,
  updateSchedule,
} from '../api/housekeeping.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, LoadFailed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';

// Settings > Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §2.4): the areas
// housekeeping cleans and each area's daily schedule (provisional H5). Areas and schedules
// are switched off, never deleted. Times are East Africa Time, 24-hour clock.

/** "07:00, 13:00" as typed, to the list the API takes. */
function timesFrom(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t !== '');
}

function ScheduleRow({
  schedule,
  online,
}: {
  schedule: HousekeepingScheduleView;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(schedule.taskName);
  const [times, setTimes] = useState(schedule.times.join(', '));
  const [active, setActive] = useState(schedule.active);
  const checked = checkScheduleTimes(timesFrom(times));
  const save = useMutation({
    mutationFn: () =>
      updateSchedule(schedule.id, {
        taskName: name.trim(),
        times: checked.ok ? checked.value : [],
        active,
      }),
    onSuccess: (view) => {
      queryClient.setQueryData(housekeepingKeys.areas, view);
      toasts.show({ title: 'Schedule saved' });
    },
  });
  return (
    <li className={styles.item}>
      <div className={styles.row}>
        <TextField label="Task" value={name} onChange={setName} maxLength={80} />
        <TextField
          label="Times"
          description="24-hour clock, separated by commas."
          value={times}
          onChange={setTimes}
          {...(checked.ok ? {} : { errorMessage: HOUSEKEEPING_ERROR_MESSAGES[checked.code] })}
        />
        <Checkbox isSelected={active} onChange={setActive}>
          In use
        </Checkbox>
      </div>
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, "It wasn't saved. Try again.")}</Notice>
      )}
      <Button
        dense
        isDisabled={!online || !checked.ok || name.trim() === ''}
        isPending={save.isPending}
        pendingLabel="Saving…"
        onPress={() => {
          save.mutate();
        }}
      >
        Save schedule
      </Button>
    </li>
  );
}

function AreaSection({ area, online }: { area: HousekeepingAreaView; online: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(area.name);
  const [taskName, setTaskName] = useState('');
  const [times, setTimes] = useState('');
  const checked = checkScheduleTimes(timesFrom(times));
  const saveArea = useMutation({
    mutationFn: (active: boolean) => updateArea(area.id, { name: name.trim(), active }),
    onSuccess: (view) => {
      queryClient.setQueryData(housekeepingKeys.areas, view);
      toasts.show({ title: 'Area saved' });
    },
  });
  const add = useMutation({
    mutationFn: () =>
      createSchedule(area.id, {
        taskName: taskName.trim(),
        times: checked.ok ? checked.value : [],
        active: true,
      }),
    onSuccess: (view) => {
      queryClient.setQueryData(housekeepingKeys.areas, view);
      setTaskName('');
      setTimes('');
      toasts.show({ title: 'Schedule added' });
    },
  });
  return (
    <Section
      title={area.name}
      actions={
        <Tag state={area.active ? 'done' : 'neutral'}>{area.active ? 'In use' : 'Not in use'}</Tag>
      }
    >
      <div className={styles.form}>
        <div className={styles.row}>
          <TextField label="Area name" value={name} onChange={setName} maxLength={80} />
          <div className={styles.actions}>
            <Button
              isDisabled={!online || name.trim() === ''}
              onPress={() => {
                saveArea.mutate(area.active);
              }}
            >
              Rename
            </Button>
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                saveArea.mutate(!area.active);
              }}
            >
              {area.active ? 'Switch off' : 'Switch on'}
            </Button>
          </div>
        </div>
        {saveArea.error === null ? null : (
          <Notice tone="error">{errorText(saveArea.error, "It wasn't saved. Try again.")}</Notice>
        )}
        <ul className={styles.list} aria-label={`Schedules in ${area.name}`}>
          {area.schedules.map((s) => (
            <ScheduleRow key={s.id} schedule={s} online={online} />
          ))}
        </ul>
        <div className={styles.row}>
          <TextField label="New task" value={taskName} onChange={setTaskName} maxLength={80} />
          <TextField
            label="Times"
            description="24-hour clock, separated by commas, like 07:00, 13:00."
            value={times}
            onChange={setTimes}
          />
        </div>
        {add.error === null ? null : (
          <Notice tone="error">{errorText(add.error, "It wasn't added. Try again.")}</Notice>
        )}
        <div className={styles.actions}>
          <Button
            isDisabled={!online || taskName.trim() === '' || !checked.ok}
            isPending={add.isPending}
            pendingLabel="Adding…"
            onPress={() => {
              add.mutate();
            }}
          >
            Add schedule
          </Button>
        </div>
      </div>
    </Section>
  );
}

export function HousekeepingSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const allowed = session !== null && can(session, 'housekeeping.manage');
  const areas = useQuery({ ...housekeepingAreasQuery, enabled: allowed });
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => createArea({ name: name.trim(), active: true }),
    onSuccess: (view) => {
      queryClient.setQueryData(housekeepingKeys.areas, view);
      setName('');
      toasts.show({ title: 'Area added' });
    },
  });
  return (
    <SettingsPage title="Housekeeping">
      {!allowed ? (
        <Notice tone="info">
          You don&apos;t have permission to change housekeeping areas. Ask an administrator if you
          need it.
        </Notice>
      ) : (
        <div className={styles.page}>
          {!online ? (
            <Notice tone="info">
              You&apos;re offline. Changes wait until you&apos;re back online.
            </Notice>
          ) : null}
          <Section title="Add an area" description="For example Outpatients, Theatre or Toilets.">
            <div className={styles.row}>
              <TextField label="Area name" value={name} onChange={setName} maxLength={80} />
              <Button
                variant="primary"
                isDisabled={!online || name.trim() === ''}
                isPending={create.isPending}
                pendingLabel="Adding…"
                onPress={() => {
                  create.mutate();
                }}
              >
                Add area
              </Button>
            </div>
            {create.error === null ? null : (
              <Notice tone="error">{errorText(create.error, "It wasn't added. Try again.")}</Notice>
            )}
          </Section>
          {areas.isPending ? <LoadingBlock label="Loading the areas" rows={3} /> : null}
          {areas.isError ? (
            <LoadFailed
              error={areas.error}
              fallback="The areas couldn't be loaded. Try again."
              onRetry={() => void areas.refetch()}
            />
          ) : null}
          {areas.isSuccess && areas.data.areas.length === 0 ? (
            <p className={styles.meta}>No areas yet. Add the first one above.</p>
          ) : null}
          {(areas.data?.areas ?? []).map((a) => (
            <AreaSection key={a.id} area={a} online={online} />
          ))}
        </div>
      )}
    </SettingsPage>
  );
}
